'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import {
  LayoutDashboard, Building2, MapPin, TrendingUp, BarChart3,
  Settings, LogOut, ChevronLeft, ChevronRight, Bell, BookMarked, Users, ShieldCheck, GitCompare, CreditCard, FileText, LayoutGrid, Layers, ClipboardEdit, Shield, RotateCcw, ScrollText, MessageSquare, Receipt, UserCircle, Inbox, UserCheck, Gift,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
  group: string;
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', icon: LayoutDashboard, href: '/dashboard', group: 'main' },
  { id: 'nav-projects', label: 'Projects', icon: Building2, href: '/project-listings-screen', badge: 214, group: 'main' },
  { id: 'nav-developers', label: 'Developers', icon: UserCheck, href: '/developers', group: 'main' },
  { id: 'nav-micromarkets', label: 'Micro Markets', icon: MapPin, href: '/micro-markets', group: 'main' },
  { id: 'nav-trends', label: 'Price Trends', icon: TrendingUp, href: '/price-trends', group: 'analytics' },
  { id: 'nav-analytics', label: 'Market Analytics', icon: BarChart3, href: '/analytics', group: 'analytics' },
  { id: 'nav-compare', label: 'Compare Projects', icon: GitCompare, href: '/project-comparison', group: 'analytics' },
  { id: 'nav-inventory', label: 'Tower Inventory', icon: Layers, href: '/tower-inventory', group: 'analytics' },
  { id: 'nav-saved', label: 'Saved Reports', icon: BookMarked, href: '/saved', badge: 3, group: 'analytics' },
  { id: 'nav-reports', label: 'PDF Reports', icon: FileText, href: '/reports', group: 'tools' },
  { id: 'nav-alerts', label: 'Alerts', icon: Bell, href: '/alerts', badge: 5, group: 'tools' },
  { id: 'nav-subscription', label: 'Subscription', icon: CreditCard, href: '/subscription', group: 'tools' },
  { id: 'nav-billing', label: 'Billing', icon: Receipt, href: '/billing', group: 'tools' },
  { id: 'nav-my-account', label: 'My Account', icon: UserCircle, href: '/my-account', group: 'tools' },
  { id: 'nav-admin', label: 'Admin Panel', icon: ShieldCheck, href: '/admin', group: 'admin', adminOnly: true },
  { id: 'nav-subscribers', label: 'Subscribers', icon: Users, href: '/subscribers', group: 'admin', adminOnly: true },
  { id: 'nav-admin-dashboard', label: 'Admin Dashboard', icon: LayoutGrid, href: '/admin-dashboard', group: 'admin', adminOnly: true },
  { id: 'nav-admin-inventory', label: 'Inventory Mgmt', icon: ClipboardEdit, href: '/admin-inventory', group: 'admin', adminOnly: true },
  { id: 'nav-admin-submissions', label: 'Form Submissions', icon: Inbox, href: '/admin-submissions', group: 'admin', adminOnly: true },
  { id: 'nav-admin-developer', label: 'Developer Editor', icon: UserCheck, href: '/admin-developer', group: 'admin', adminOnly: true },
  { id: 'nav-admin-referrals', label: 'Referral Admin', icon: Gift, href: '/admin-referrals', group: 'admin', adminOnly: true },
  { id: 'nav-privacy', label: 'Privacy Policy', icon: Shield, href: '/privacy-policy', group: 'legal' },
  { id: 'nav-refund', label: 'Refund Policy', icon: RotateCcw, href: '/refund-policy', group: 'legal' },
  { id: 'nav-terms', label: 'Terms & Conditions', icon: ScrollText, href: '/terms-and-conditions', group: 'legal' },
  { id: 'nav-contact', label: 'Contact Us', icon: MessageSquare, href: '/contact-us', group: 'legal' },
];

const groups = [
  { key: 'main', label: 'Research' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'tools', label: 'Tools' },
  { key: 'admin', label: 'Admin' },
  { key: 'legal', label: 'Legal' },
];

interface SidebarProps {
  activeRoute?: string;
}

export default function Sidebar({ activeRoute }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { profile, user, isAdmin, signOut } = useAuth();
  const router = useRouter();

  const displayName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
  const planLabel = isAdmin ? 'Admin Account' : 'Yearly Plan · Renews Apr 2027';

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/sign-up-login-screen');
    } catch {
      toast.error('Failed to sign out');
    }
  };

  const visibleItems = (items: NavItem[]) =>
    isAdmin ? items : items.filter((item) => !item.adminOnly);

  return (
    <aside
      className={`relative flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out shrink-0 ${
        collapsed ? 'w-16' : 'w-60'
      } hidden lg:flex`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-4 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-base text-foreground tracking-tight whitespace-nowrap">
            PropdataX
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
        {groups.map((group) => {
          const items = visibleItems(navItems.filter((n) => n.group === group.key));
          if (!items.length) return null;
          return (
            <div key={`group-${group.key}`} className="mb-4">
              {!collapsed && (
                <p className="px-4 mb-1 text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">
                  {group.label}
                </p>
              )}
              {items.map((item) => {
                const Icon = item.icon;
                const isActive = activeRoute === item.href;
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`flex items-center gap-3 mx-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 group relative ${
                      isActive
                        ? 'bg-amber-500/10 text-amber-400' :'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                    }`}
                  >
                    <Icon size={18} className="shrink-0" />
                    {!collapsed && (
                      <>
                        <span className="flex-1 whitespace-nowrap">{item.label}</span>
                        {item.badge !== undefined && (
                          <span className="bg-amber-500/20 text-amber-400 text-[10px] font-semibold px-1.5 py-0.5 rounded">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                    {collapsed && item.badge !== undefined && (
                      <span className="absolute top-1 right-1 w-2 h-2 bg-amber-500 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-3 space-y-1">
        <Link
          href="/settings"
          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
        >
          <Settings size={18} />
          {!collapsed && <span>Settings</span>}
        </Link>
        <button
          onClick={handleSignOut}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-150 ${collapsed ? 'justify-center' : ''}`}
        >
          <LogOut size={18} />
          {!collapsed && <span>Sign Out</span>}
        </button>
        {!collapsed && (
          <div className="mt-2 mx-1 p-2.5 bg-muted/40 rounded-lg">
            <p className="text-[11px] text-muted-foreground font-medium">{displayName}</p>
            <p className="text-[10px] text-muted-foreground/60">{planLabel}</p>
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="absolute -right-3 top-16 w-6 h-6 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-amber-500/50 transition-all duration-150 z-10"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>
    </aside>
  );
}